import { WorkspaceFindForm } from "../../Form/WorkspaceFindForm"
import { useState, useEffect, useRef } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import Icon from "../../Components/Icon"
import Text from "../../Components/Text"
import { useModal } from "../../Components/Modal";
import Button from "../../Components/Buttons/Button"
import TIcon from "../../Components/TIcon"
import CookieInfo from "../../Components/CookieInfo"
import Workspace from "../../APIController/Workspace"
import ItemScrollbar from "../../Components/ItemScrollbar"
import { SimpleNumberInput } from "../../Components/Form/SimpleNumberInput"
import { CheckoutModal } from "./CheckoutModal"
import Snackbar from "../../Components/Elements/Snackbar"
import { GoogleMapsLocation } from "../../Components/GoogleMapsLocation"

export const SearchPage = () => {
    const navigate = useNavigate();
    const { findWorkspaceNear, getCategories, getProducts } = Workspace()
    const { openModal, ModalView, closeModal } = useModal()
    const [searchParams] = useSearchParams()
    const [address, setAddress] = useState("")
    const [rad, setRad] = useState(1)
    const [workspaces, setWorkspaces] = useState([])
    const [realAddress, setRealAddress] = useState(localStorage.getItem("lastAddress") || "")
    const [selectedWS, setSelectedWS] = useState(localStorage.getItem("lastWS") ? JSON.parse(localStorage.getItem("lastWS")) : null)
    const [categories, setCategories] = useState([])
    const [products, setProducts] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [orderProducts, setOrderProducts] = useState([])
    const [snackbarMessage, setSnackbarMessage] = useState("")
    const categoryRefs = useRef({})

    // LocalStorage Helper
    const saveToLocalStorage = (lat, lon, address) => {
        localStorage.setItem("lastAddress", address);
        localStorage.setItem("lastLat", lat);
        localStorage.setItem("lastLon", lon);
    }

    const saveLastWS = (ws) => {
        localStorage.setItem("lastWS", JSON.stringify(ws));
    }

    const removeLastWS = () => {
        localStorage.removeItem("lastWS");
    }

    // Order Helper
    const updateProductCount = (productId, newCount) => {
        if (newCount <= 0) {
            setOrderProducts(prev => prev.filter(p => p.productId !== productId))
        } else {
            setOrderProducts(prev => {
                const existing = prev.find(p => p.productId === productId)
                if (existing) {
                    return prev.map(p => p.productId === productId ? { ...p, count: newCount } : p)
                } else {
                    return [...prev, { productId, count: newCount }]
                }
            })
        }
    }

    const getProductCount = (productId) => {
        return orderProducts.find(p => p.productId === productId)?.count || 0
    }

    // Effects
    useEffect(() => {
        const lat = localStorage.getItem("lastLat");
        const lon = localStorage.getItem("lastLon");
        if (lat && lon) {
            loadWS(lat, lon)
        }
        setLoaded(true)
    }, [])

    const loadWS = async (lat, lon) => {
        const data = await findWorkspaceNear(lat, lon, rad)
        setWorkspaces(data)

        const lastWS = localStorage.getItem("lastWS")
        if (lastWS) {
            const lastWSId = JSON.parse(lastWS).id
            const found = data.some(ws => ws.id === lastWSId)
            if (!found) {
                localStorage.removeItem("lastWS")
                resetWSSelection()
            }
        }
    }

    useEffect(() => {
        const urlAddress = searchParams.get("address")
        const urlRad = searchParams.get("rad")
        if (urlAddress) setAddress(urlAddress)
        if (urlRad) setRad(Number(urlRad))
    }, [searchParams])

    useEffect(() => {
        const accepted = localStorage.getItem("cookiesAccepted");
        if (!accepted) {
            openModal(<CookieInfo onAccept={acceptCookies} />);
        }
    }, []);

    const acceptCookies = () => {
        localStorage.setItem("cookiesAccepted", "true");
        closeModal();
    };

    useEffect(() => {
        if (selectedWS) {
            fetchData(selectedWS)
        }
    }, [selectedWS])

    const fetchData = async (ws) => {
        const cat = await getCategories(ws.id)
        setCategories(cat)
        const prod = await getProducts(ws.id)
        setProducts(prod)
    }

    const afterAction = (data, address, lat, lon) => {
        setWorkspaces(data)
        setRealAddress(address)
        saveToLocalStorage(lat, lon, address)
        resetWSSelection()
        removeLastWS()
        closeModal()
    }

    const resetWSSelection = () => {
        setCategories([])
        setProducts([])
        setSelectedWS(null)
        setOrderProducts([])
        removeLastWS()
    }

    const openCheckout = () => {
        const user = localStorage.getItem("user")
        if (!user) {
            navigate("/login")
        } else {
            openModal(() => <CheckoutModal
                orderProducts={orderProducts}
                products={products}
                wsID={selectedWS.id}
                erroraction={(error) => { closeModal(); setSnackbarMessage(error) }}
                afterAction={() => { closeModal(); setSnackbarMessage("Bestellung abgesendet") }} />)
        }
    }

    const getCurrentLogo = (workspace) => workspace ? workspace.logo : null

    const typeToString = (type) => {
        switch (type) {
            case "CAFFEE": return "Café"
            case "BAR": return "Bar"
            case "RESTAURANT": return "Restaurant"
            case "FOOD_TRUCK": return "Food Truck"
            case "FOOD_STAND": return "Essensstand"
            case "NOT_DEFINED":
            default: return ""
        }
    }

    // UI
    const WorkspaceCard = ({ workspace }) => (
        <div className="w-full flex flex-row bg-gray-100 gap-2 p-4 rounded-lg shadow-md" onClick={() => { setSelectedWS(workspace); saveLastWS(workspace); }}>
            {getCurrentLogo(workspace) && <img src={getCurrentLogo(workspace)} alt="Logo" className="h-12 w-12 object-cover rounded-md mr-2" />}
            <div className="w-full flex flex-col gap-2">
                <Text type="title">{workspace.name}</Text>
                <div className="w-full flex gap-2">
                    <TIcon name={"clock"} />
                    <Text>{workspace.openTime?.slice(0, 5)} - {workspace.closeTime?.slice(0, 5)}</Text>
                </div>
                {workspace.type && workspace.type !== "NOT_DEFINED" && (
                    <div className="w-full flex gap-2">
                        <TIcon name={"building-store"} />
                        <Text>{typeToString(workspace.type)}</Text>
                    </div>
                )}
                <div className="w-full flex gap-2">
                    <TIcon name={"map-pin"} />
                    <Text>{workspace.plz} {workspace.street} {workspace.city}</Text>
                </div>
            </div>
        </div>
    )

    const formWrapper = () => (
        <WorkspaceFindForm
            preAddress={address}
            preRad={rad}
            resultAction={(d, a, lat, lon) => afterAction(d, a, lat, lon)}
        />
    )

    const searchHeader = () => (
        <Button onClickAction={() => openModal(() => formWrapper())} borderStyle="border rounded-lg shadow-lg">
            <TIcon name={"map-pin"} />
            <Text>{realAddress ? realAddress : "Bitte Addresse angeben"}</Text>
        </Button>
    )

    const wsHeader = () => (
        <div className="w-full flex pr-3">
            <Button tIconName={"chevron-left"} onClickAction={() => resetWSSelection()} />
            <div className="w-full flex justify-center gap-3">
                <Text type="maintitle">{selectedWS.name}</Text>
            </div>
            <Button tIconName={"shopping-cart"} classNames="p-3" onClickAction={() => openCheckout()} />
        </div>
    )

    const scrollToCategory = (category) => {
        const el = categoryRefs.current[category.id]
        if (!el) return
        el.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    const productBox = (product) => (
        <div key={product.id} className="w-full flex flex-row bg-gray-100 gap-2 p-4 rounded-lg shadow-md">
            <div className="flex flex-col w-full gap-2">
                <Text type="itemmain">{product.name}</Text>
                <Text type="itemtext">{product.price} CHF</Text>
            </div>
            <div className="h-full items-center justify-center flex flex-col">
                <SimpleNumberInput
                    value={getProductCount(product.id)}
                    onChange={(value) => updateProductCount(product.id, value)}
                    min={0}
                    step={1}
                />
            </div>
        </div>
    )

    const productOverview = (category) => {
        const categoryProducts = products.filter(p => p.category?.id === category.id)
        if (categoryProducts.length === 0) return null

        return (
            <div
                key={category.id}
                ref={(el) => (categoryRefs.current[category.id] = el)}
                className="w-full flex flex-col gap-3"
            >
                <Text type="main">{category.name}</Text>
                <div className="w-full flex flex-col gap-2">
                    {categoryProducts.map(product => productBox(product))}
                </div>
            </div>
        )
    }

    const wsDetailsBody = () => (
        loaded && (
            <div className="w-full h-full flex flex-col gap-3 border-x border-gray-300 px-3">
                <div>
                    <WorkspaceCard key={`${selectedWS.id}`} workspace={selectedWS} />
                </div>
                <GoogleMapsLocation
                    street={selectedWS.street}
                    postalCode={selectedWS.plz}
                    city={selectedWS.city}
                    hidden={!selectedWS.street && !selectedWS.plz && !selectedWS.city} />
                <Text type="main">Speisekarte</Text>
                <div className="flex h-fit">
                    <ItemScrollbar
                        itemType={"Kategorien"}
                        items={categories}
                        itemDisplayOption={(item) => item.name}
                        colorDisplayOption={(item) => item.colorCode}
                        color="bg-transparent"
                        onClickAction={scrollToCategory}
                        borderOptions="" />
                </div>

                <div className="w-full h-full flex flex-col gap-6">
                    {categories?.map(category => productOverview(category))}
                </div>
            </div>
        )
    )

    const wsOverview = () => (
        <div className="h-full w-full gap-3 flex">
            <div className="h-full w-1/4 border-r border-gray-300">
                <Text type="main">Filter</Text>
            </div>
            <div className="h-full w-3/4">
                <div className="w-full h-full flex flex-col gap-2">
                    {workspaces.map((ws) => (
                        <WorkspaceCard key={`${ws.id}`} workspace={ws} />
                    ))}
                </div>
            </div>
        </div>
    )

    return (
        <div className="h-full w-full flex flex-col bg-white pb-8">
            <div className="w-full flex bg-orange-300 shrink-0">
                <Icon iconName={"s2g_icon.png"} classNames="w-20 h-20" />
                <div className="h-full w-full flex items-center justify-center">
                    {selectedWS ? wsHeader() : searchHeader()}
                </div>
            </div>

            <div className="flex-1 w-full flex justify-center overflow-y-auto">
                <div className="w-full md:w-2/3 p-5">
                    {selectedWS ? wsDetailsBody() : wsOverview()}
                </div>
            </div>

            <ModalView />
            <Snackbar message={snackbarMessage} onClose={() => setSnackbarMessage("")} />
        </div>
    )
}